import client from "shared/utils/axios-config";

/**
 * Step 1 of the System Admin's add-account modal: classify a phone number
 * before deciding which second step to show.
 *
 * A plain promise rather than a useQuery, like getStaffLookup: this is an
 * imperative action fired by the Continue button, not state a screen renders.
 *
 * business_id travels as a query param because a system admin's token carries
 * none, which is also why this lives on /v1/admin instead of reusing
 * /v1/user/staff-lookup (that one sits behind isAdminMiddleware and would 401
 * a system admin, and the axios interceptor turns a 401 into a forced logout).
 *
 * The statuses, in the server's evaluation order:
 *  - new                  no account with this number
 *  - deleted              the account was permanently deleted
 *  - systemadmin          a Spott team account; never a studio admin
 *  - staff_elsewhere      staff at another business (staff are single-business)
 *  - admin_here           already an admin of this business
 *  - staff_inactive_here  a deactivated staff member here; linking revives them
 *  - staff_here           an active instructor here
 *  - known                exists, no staff row anywhere (a client, typically)
 *
 * full_name comes back for everything the modal can confirm; has_password and
 * is_verified drive the conditional password field and the OTP note.
 */
export interface AdminLookupResult {
  status:
    | "new"
    | "deleted"
    | "systemadmin"
    | "staff_elsewhere"
    | "admin_here"
    | "staff_inactive_here"
    | "staff_here"
    | "known";
  full_name?: string;
  has_password?: boolean;
  is_verified?: boolean;
}

const getAdminLookup = async (
  businessId: string,
  phoneNumber: string,
): Promise<AdminLookupResult> => {
  const res = await client.get("/v1/admin/lookup", {
    params: { business_id: businessId, phone_number: phoneNumber },
  });
  return res.data.data;
};

export { getAdminLookup };
